/**
 * Phase 7.1 audit-trail panel — operator-visible read-back of their own
 * recent events. Mirrors the design doc's "operator-visibility" section:
 * the operator sees THEIR OWN audit trail (a team-lead variant comes in
 * Phase 7.3; compliance-officer variant in 7.4).
 *
 * Lives as a panel rendered by the Operator Workspace (§7.23). v1 is
 * read-only — no editing of past events (append-only contract).
 */

import { useEffect, useState } from 'react';
import { auditCapture, getActiveStoreLabel, getTenantContext } from './capture';
import type { AuditEvent } from './types';

/** Display labels per event type. */
const EVENT_LABEL: Record<string, string> = {
  'session.start': 'Session started',
  'session.end': 'Session ended',
  'surface.view': 'Opened surface',
  'signal.detail.open': 'Opened signal detail',
  'pin.add': 'Pinned',
  'pin.remove': 'Removed pin',
  'annotation.create': 'Created annotation',
  'annotation.edit': 'Edited annotation',
  'annotation.delete': 'Deleted annotation',
  'alert-rule.create': 'Created alert rule',
  'alert-rule.edit': 'Edited alert rule',
  'alert-rule.toggle': 'Toggled alert rule',
  'alert-rule.delete': 'Deleted alert rule',
  'inbox.acknowledge': 'Acknowledged inbox item',
  'inbox.snooze': 'Snoozed inbox item',
  'inbox.dismiss': 'Dismissed inbox item',
  'briefing.export': 'Exported briefing',
  'scenario.change': 'Changed scenario',
  'persona.switch': 'Switched persona',
  'source.degraded': 'Source went DEGRADED',
  'source.recovered': 'Source recovered',
};

export function AuditPanel() {
  const [events, setEvents] = useState<AuditEvent[]>(() => auditCapture.list({ limit: 50 }));

  // Refresh every 2s so the panel reflects events emitted while open.
  // Phase 7.2 will replace the poll with a real event-emitter subscription.
  useEffect(() => {
    const interval = setInterval(() => {
      setEvents(auditCapture.list({ limit: 50 }));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      <header style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 680 }}>Audit trail</h2>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-subtle)', marginTop: 3,
        }}>
          your own recent activity · tenant <b style={{ color: 'var(--bm-text)' }}>{getTenantContext().tenant}</b>
          {' '}· {getActiveStoreLabel()} ·
          showing latest {Math.min(events.length, 50)} of {events.length}
        </div>
      </header>

      {events.length === 0 ? (
        <div style={{
          padding: '20px 14px', textAlign: 'center',
          border: '1px dashed var(--bm-border)', borderRadius: 'var(--bm-radius-md)',
          fontSize: 13, color: 'var(--bm-text-subtle)',
        }}>
          No events yet this session — pin something, ack an inbox item, or
          switch personas to see the trail populate.
        </div>
      ) : (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 4,
          maxHeight: 480, overflowY: 'auto',
        }}>
          {events.map(e => (
            <article
              key={e.id}
              style={{
                display: 'grid', gridTemplateColumns: '170px 200px 1fr', gap: 12,
                padding: '8px 12px',
                background: 'var(--bm-bg-subtle)',
                border: '1px solid var(--bm-border)',
                borderRadius: 'var(--bm-radius-sm)',
                fontSize: 12, lineHeight: 1.4,
              }}
            >
              <span style={{ fontFamily: 'var(--bm-font-mono)', color: 'var(--bm-text-subtle)' }}>
                {e.asOf.slice(0, 19).replace('T', ' ')}
              </span>
              <span style={{ fontWeight: 600, color: e.event.type.startsWith('source.') ? 'var(--bm-chg-degraded)' : 'var(--bm-text)' }}>
                {EVENT_LABEL[e.event.type] ?? e.event.type}
              </span>
              <span style={{ color: 'var(--bm-text-muted)' }}>
                {e.event.surface && (
                  <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)', marginRight: 6 }}>
                    {e.event.surface}
                  </span>
                )}
                {summarisePayload(e)}
              </span>
            </article>
          ))}
        </div>
      )}

      <footer style={{
        marginTop: 12, fontSize: 11, color: 'var(--bm-text-subtle)', lineHeight: 1.5,
      }}>
        Events are append-only — there is no edit or delete. Per Phase 7
        governance (PII redaction): no annotation contents, briefing payloads,
        or session IPs are logged. Only domain identifiers + counts.
      </footer>
    </div>
  );
}

/** One-line summary of the payload for a given event. Per PII rules,
 *  NEVER renders annotation contents / briefing text — only counts +
 *  opaque ids. */
function summarisePayload(e: AuditEvent): string {
  const p = e.event.payload;
  const ids = e.event.subjectIds;
  switch (e.event.type) {
    case 'pin.add':
    case 'pin.remove':
    case 'inbox.acknowledge':
    case 'inbox.snooze':
    case 'inbox.dismiss':
      return ids.length === 1 ? `id ${ids[0]}` : `${ids.length} items`;
    case 'briefing.export':
      return `${p['format'] ?? 'unknown'} · ${ids.length} items`;
    case 'annotation.create':
    case 'annotation.edit':
    case 'annotation.delete':
      return `annotation ${ids[0] ?? '?'}${p['lengthBucket'] ? ` · ${p['lengthBucket']}` : ''}`;
    case 'alert-rule.create':
    case 'alert-rule.edit':
    case 'alert-rule.toggle':
    case 'alert-rule.delete':
      return `rule ${ids[0] ?? '?'}${typeof p['on'] === 'boolean' ? ` · ${p['on'] ? 'on' : 'off'}` : ''}`;
    case 'persona.switch':
      return `${p['from'] ?? '?'} → ${p['to'] ?? '?'}`;
    case 'surface.view':
      return `from ${p['from'] ?? '?'}`;
    case 'scenario.change':
      return `${p['key'] ?? '?'} → ${p['value'] ?? '?'}`;
    case 'source.degraded':
    case 'source.recovered':
      return `source ${ids[0] ?? '?'}`;
    default:
      return '';
  }
}
